/**
 * Scoring taxonomy contract — frontend (ES modules).
 *
 * MIRROR: keep in sync with functions/contracts/taxonomy.js
 * Any change to a category/subcategory key, class, weight, or regimen tag MUST
 * update both copies in the same commit.
 *
 * Source of truth: docs/phase-0 scoring-categories-and-regimens.md (blueprint §5.1).
 * The Oracle scores each SUBCATEGORY 0-100; CATEGORY scores are deterministic
 * weighted rollups (see fuseScores on the backend / rollupCategoryScore here).
 *
 * Modifiability class (drives the "what you can change" panel + the product engine):
 *   mod     — Modifiable: regimen/products genuinely move it (carries products)
 *   partial — Partial: appearance improves but capped by genetics (carries products, honest ceiling)
 *   fixed   — Fixed: bone/structural; scored, never coached, never sold against
 *   mixed   — Varies by cause (e.g. dark circles); resolve subtype before coaching
 *   base    — Foundational routine layer (sunscreen/moisturizer); not a scored subcategory
 *
 * @typedef {("mod"|"partial"|"fixed"|"mixed"|"base")} ModClass
 *
 * @typedef {Object} Category
 * @property {string} key
 * @property {string} label
 * @property {number} weight           // category weight in the overall rollup (sum ~= 1)
 * @property {boolean} carries_products
 *
 * @typedef {Object} Subcategory
 * @property {string} key
 * @property {string} label
 * @property {string} category         // Category.key
 * @property {ModClass} cls
 * @property {string} timeline         // "" for fixed; else Days | Weeks | 1-3 mo | 3-6 mo+
 * @property {string[]} regimen_tags   // active/ladder tags the product engine resolves; [] for fixed
 */

export const MOD_CLASSES = Object.freeze(["mod", "partial", "fixed", "mixed", "base"]);

// Display palette — mirrors the design prototype's CLS map.
export const MODIFIABILITY_META = Object.freeze({
  mod:     { label: "MODIFIABLE",   note: "in your plan",      color: "#E6C083", dot: "#C9A45C" },
  partial: { label: "PARTIAL",      note: "caps at a ceiling", color: "#C9A77A", dot: "#9A7B40" },
  fixed:   { label: "FIXED",        note: "bone structure",    color: "#8A857C", dot: "#56524A" },
  mixed:   { label: "VARIES",       note: "depends on cause",  color: "#C9A77A", dot: "#9A7B40" },
  base:    { label: "FOUNDATIONAL", note: "every plan",        color: "#B8B2A6", dot: "#8A857C" },
});

export const CATEGORIES = Object.freeze([
  { key: "skin_quality",   label: "Skin Quality",     weight: 0.28, carries_products: true },
  { key: "hair_scalp",     label: "Hair",             weight: 0.14, carries_products: true },
  { key: "facial_hair",    label: "Facial Hair",      weight: 0.08, carries_products: true },
  { key: "brows_lashes",   label: "Brows & Lashes",   weight: 0.08, carries_products: true },
  { key: "mouth_smile",    label: "Mouth & Smile",    weight: 0.10, carries_products: true },
  { key: "definition",     label: "Facial Definition", weight: 0.12, carries_products: true },
  { key: "bone_structure", label: "Bone Structure",   weight: 0.20, carries_products: false },
]);

// One row per doc subcategory. `regimen_tags` is Gemini's `active` allow-list;
// `areas` joins to the live `products` catalog (the dotted `area` field). Subcats
// whose areas carry no catalog products fall through to behavioral coaching.
export const SUBCATEGORIES = Object.freeze([
  // 1. Skin Quality
  { key: "acne",          label: "Acne & breakouts",  category: "skin_quality", cls: "mod",     timeline: "Weeks-3 mo", regimen_tags: ["bha", "bpo", "retinoid", "azelaic", "niacinamide"], areas: ["skin.acne"] },
  { key: "texture",       label: "Texture",           category: "skin_quality", cls: "mod",     timeline: "1-3 mo+",    regimen_tags: ["retinoid", "aha", "vitc"], areas: ["skin.texture"] },
  { key: "tone",          label: "Tone & pigment",    category: "skin_quality", cls: "mod",     timeline: "3-6 mo+",    regimen_tags: ["vitc", "niacinamide", "azelaic", "retinoid", "sunscreen_base"], areas: ["skin.tone_pigment"] },
  { key: "redness",       label: "Redness",           category: "skin_quality", cls: "partial", timeline: "Weeks-3 mo", regimen_tags: ["azelaic", "niacinamide", "rosacea"], areas: ["skin.redness"] },
  { key: "hydration",     label: "Hydration & glow",  category: "skin_quality", cls: "mod",     timeline: "Days",       regimen_tags: ["hydration_base", "niacinamide"], areas: ["skin.hydration"] },
  { key: "oiliness",      label: "Oiliness",          category: "skin_quality", cls: "mod",     timeline: "Weeks",      regimen_tags: ["niacinamide", "retinoid", "bha"], areas: ["skin.oiliness"] },
  { key: "pores",         label: "Pores",             category: "skin_quality", cls: "partial", timeline: "1-3 mo",     regimen_tags: ["retinoid", "bha", "niacinamide"], areas: ["skin.pores"] },
  { key: "dark_circles",  label: "Under-eye circles", category: "skin_quality", cls: "mixed",   timeline: "Mixed",      regimen_tags: ["hydration_base", "vitc", "habit"], areas: ["skin.dark_circles.vascular", "skin.dark_circles.pigmentary"] },
  // 2. Hair (Scalp)
  { key: "hairline",      label: "Hairline",          category: "hair_scalp",   cls: "mod",     timeline: "3-6 mo+",    regimen_tags: ["minoxidil", "ketoconazole"], areas: ["hair.recession_density"] },
  { key: "hair_density",  label: "Density",           category: "hair_scalp",   cls: "partial", timeline: "3-6 mo+",    regimen_tags: ["minoxidil"], areas: ["hair.recession_density"] },
  { key: "hair_condition", label: "Condition",        category: "hair_scalp",   cls: "mod",     timeline: "Weeks",      regimen_tags: ["hair_care"], areas: ["hair.condition"] },
  { key: "hair_cut",      label: "Cut & styling",     category: "hair_scalp",   cls: "mod",     timeline: "Days",       regimen_tags: ["styling"], areas: ["hair.styling"] },
  // 3. Facial Hair
  { key: "beard_shaping", label: "Shaping",           category: "facial_hair",  cls: "mod",     timeline: "Days",       regimen_tags: ["beard_tools"], areas: ["beard.shaping"] },
  { key: "beard_density", label: "Density",           category: "facial_hair",  cls: "partial", timeline: "3-6 mo+",    regimen_tags: ["minoxidil"], areas: ["beard.density"] },
  { key: "beard_condition", label: "Condition",       category: "facial_hair",  cls: "mod",     timeline: "Weeks",      regimen_tags: ["beard_oil"], areas: ["beard.condition"] },
  // 4. Brows & Lashes
  { key: "brow_shape",    label: "Brow shape",        category: "brows_lashes", cls: "mod",     timeline: "Days",       regimen_tags: ["brow_grooming"], areas: ["brow.shape"] },
  { key: "brow_density",  label: "Brow density",      category: "brows_lashes", cls: "partial", timeline: "1-3 mo",     regimen_tags: ["minoxidil", "bimatoprost"], areas: ["brow.density"] },
  { key: "lash_density",  label: "Lash density",      category: "brows_lashes", cls: "partial", timeline: "1-3 mo",     regimen_tags: ["bimatoprost"], areas: ["lash.density"] },
  // 5. Mouth / Smile
  { key: "teeth_whiteness", label: "Teeth whiteness", category: "mouth_smile",  cls: "mod",     timeline: "Weeks",      regimen_tags: ["teeth_whitening"], areas: ["teeth.whiteness"] },
  { key: "teeth_alignment", label: "Teeth alignment", category: "mouth_smile",  cls: "mod",     timeline: "3-6 mo+",    regimen_tags: ["orthodontics"], areas: ["teeth.alignment"] },
  { key: "gum_health",    label: "Gum & oral health", category: "mouth_smile",  cls: "mod",     timeline: "Weeks",      regimen_tags: ["oral_care"], areas: ["oral.health"] },
  { key: "lip_condition", label: "Lip condition",     category: "mouth_smile",  cls: "mod",     timeline: "Days",       regimen_tags: ["lip_care"], areas: ["lips.condition"] },
  { key: "lip_shape",     label: "Lip shape",         category: "mouth_smile",  cls: "fixed",   timeline: "",           regimen_tags: [], areas: [] },
  // 6. Facial Definition
  { key: "facial_fat",    label: "Definition",        category: "definition",   cls: "mod",     timeline: "1-3 mo+",    regimen_tags: ["habit"], areas: ["definition.body_fat"] },
  { key: "water_retention", label: "Puffiness",       category: "definition",   cls: "mod",     timeline: "Days",       regimen_tags: ["habit"], areas: ["definition.water_retention"] },
  { key: "undereye_puffiness", label: "Under-eye puffiness", category: "definition", cls: "mod", timeline: "Days",     regimen_tags: ["habit"], areas: ["definition.under_eye_puffiness"] },
  { key: "posture",       label: "Posture & jawline", category: "definition",   cls: "mod",     timeline: "Weeks-3 mo", regimen_tags: ["habit"], areas: ["definition.posture"] },
  // 7. Bone Structure (FIXED — scored, never coached, no products)
  { key: "jaw_width",     label: "Jaw width",         category: "bone_structure", cls: "fixed", timeline: "",           regimen_tags: [], areas: [] },
  { key: "chin_projection", label: "Chin projection", category: "bone_structure", cls: "fixed", timeline: "",          regimen_tags: [], areas: [] },
  { key: "cheekbones",    label: "Cheekbones",        category: "bone_structure", cls: "fixed", timeline: "",           regimen_tags: [], areas: [] },
  { key: "brow_ridge",    label: "Brow ridge",        category: "bone_structure", cls: "fixed", timeline: "",           regimen_tags: [], areas: [] },
  { key: "facial_thirds", label: "Facial thirds",     category: "bone_structure", cls: "fixed", timeline: "",           regimen_tags: [], areas: [] },
  { key: "eye_structure", label: "Eye structure",     category: "bone_structure", cls: "fixed", timeline: "",           regimen_tags: [], areas: [] },
  { key: "nose",          label: "Nose shape",        category: "bone_structure", cls: "fixed", timeline: "",           regimen_tags: [], areas: [] },
  { key: "symmetry",      label: "Symmetry",          category: "bone_structure", cls: "fixed", timeline: "",           regimen_tags: [], areas: [] },
]);

export const CATEGORY_KEYS = Object.freeze(CATEGORIES.map((c) => c.key));
export const SUBCATEGORY_KEYS = Object.freeze(SUBCATEGORIES.map((s) => s.key));

const SUBCAT_BY_KEY = Object.freeze(
  SUBCATEGORIES.reduce((acc, s) => { acc[s.key] = s; return acc; }, {}),
);
const CAT_BY_KEY = Object.freeze(
  CATEGORIES.reduce((acc, c) => { acc[c.key] = c; return acc; }, {}),
);

export function getSubcategory(key) { return SUBCAT_BY_KEY[key] || null; }
export function getCategory(key) { return CAT_BY_KEY[key] || null; }
export function subcatsByCategory(catKey) {
  return SUBCATEGORIES.filter((s) => s.category === catKey);
}

/** Subcategories the product engine may act on: Modifiable/Partial, never bone. */
export function productDrivingSubcats() {
  return SUBCATEGORIES.filter(
    (s) => (s.cls === "mod" || s.cls === "partial") && s.category !== "bone_structure",
  );
}

/**
 * Deterministic category rollup: mean of its subcategory scores (equal weight).
 * @param {Record<string, number>} subScores  map subcat key -> 0..100
 * @returns {Array<{key:string, score:number}>}  one entry per category
 */
export function rollupCategoryScores(subScores) {
  return CATEGORIES.map((c) => {
    const subs = subcatsByCategory(c.key);
    const vals = subs
      .map((s) => subScores[s.key])
      .filter((v) => typeof v === "number" && Number.isFinite(v));
    const score = vals.length
      ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
      : 0;
    return { key: c.key, score };
  });
}

/**
 * Overall 1-100 from category scores, weighted by Category.weight.
 * @param {Array<{key:string, score:number}>} categoryScores
 */
export function rollupOverall(categoryScores) {
  let sum = 0;
  let wsum = 0;
  for (const cs of categoryScores) {
    const cat = CAT_BY_KEY[cs.key];
    if (!cat) continue;
    sum += cs.score * cat.weight;
    wsum += cat.weight;
  }
  if (!wsum) return 1;
  return Math.min(100, Math.max(1, Math.round(sum / wsum)));
}
